// src/hooks/useAdminApiHooks.ts
import {
  gxBanklistApi,
  gxContactValidateApi,
  gxRolePermisionsApi,
  gxSmeGlobalCountryApi,
  gxSmeFileUploadApi,
  gxThirdPartySubFeatureUpdate,
  gxThirdPartySubFeatureView,
} from "../apiConfig/services/customerAbstractApiLayer";
import {
  gxAuthMatrixGroupViewApi,
  gxAuthMatrixRolePermissionValidateApi,
} from "../apiConfig/services/adminMenuAbstractApiLayers";
import { useApiHook, useApiMutation } from "./useCommonApi";


// ------------------- Specific Hooks (auto-fetch on mount / dependency change) -------------------

// Bank List
export const useBankList = (payload: any, enabled = true) =>
  useApiHook(gxBanklistApi, payload, enabled);

// Role Permissions
export const useRolePermissions = (payload: any, enabled = true) =>
  useApiHook(gxRolePermisionsApi, payload, enabled);

// Contact Validate
export const useContactValidate = (payload: any, enabled = true) =>
  useApiHook(gxContactValidateApi, payload, enabled);

// Country List
export const useGlobalCountry = (payload: any, enabled = true) =>
  useApiHook(gxSmeGlobalCountryApi, payload, enabled);

// Auth Groups - Auto-fetch on mount
export const useAuthGroups = (payload: any = {}, enabled = true) =>
  useApiHook(gxAuthMatrixGroupViewApi, payload, enabled);

// Auth Matrix Role Permission Validate - fires on mount and whenever payload changes
export const useAuthMatrixRolePermissionValidate = (
  payload: { id: number },
  enabled = true,
) => useApiHook(gxAuthMatrixRolePermissionValidateApi, payload, enabled);

// ------------------- Specific Mutation Hooks -------------------

/**
 * Reusable across any third-party connection screen (ZohoErp, Stripe,
 * Magnati, and any future integrations) that submits a
 * compConfigFeatureFeeList-based payload to gxThirdPartySubFeatureUpdate.
 *
 * Usage:
 * const { mutate: updateSubFeature, loading: isSubmitting } =
 *   useThirdPartySubFeatureUpdate();
 *
 * const responseData = await updateSubFeature(payload);
 */
export const useThirdPartySubFeatureUpdate = () =>
  useApiMutation(gxThirdPartySubFeatureUpdate);

/**
 * Fetch third-party sub-feature details manually.
 *
 * Usage:
 * const {
 *   mutate: viewSubFeature,
 *   loading,
 *   data,
 *   error,
 * } = useThirdPartySubFeatureView();
 *
 * const response = await viewSubFeature({
 *   featureId: "123",
 * });
 */
export const useThirdPartySubFeatureView = () =>
  useApiMutation(gxThirdPartySubFeatureView);

/**
 * Uploads an SME document (multipart/form-data).
 *
 * Usage:
 * const { mutate: uploadSmeFile, loading: isUploading } = useSmeFileUpload();
 *
 * const formData = new FormData();
 * formData.append("type", "update");
 * formData.append("vcifId", vcifId);
 * formData.append("documentType", "SHARE HOLDER DOCUMENT");
 * formData.append("file", file);
 * formData.append("countryCode2", countryCode2);
 *
 * const responseData = await uploadSmeFile(formData);
 */
export const useSmeFileUpload = () => useApiMutation(gxSmeFileUploadApi);